import { useState } from "react";
import { formatDateTime } from "@/lib/format";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { AppLayout } from "@/components/AppLayout";
import { useCreateTicket, useSupportSubscription, useTickets } from "@/lib/support";
import { TicketStatusPill } from "@/modules/support/ticketStatus";

export default function SupportTicketsPage() {
  const { t } = useTranslation();
  const { data: tickets, isLoading } = useTickets();
  const { data: sub } = useSupportSubscription();
  const [showForm, setShowForm] = useState(false);

  const slaLine = () => {
    if (!sub) return "";
    const h = sub.plan.first_response_sla_hours;
    if (h === 0) return t("support.sla.instant");
    if (h >= 168) return t("support.sla.days", { count: Math.round(h / 24) });
    return t("support.sla.hours", { count: h });
  };

  return (
    <AppLayout
      title={t("support.tickets.title")}
      description={t("support.tickets.subtitle")}
    >
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-nb-text-2">
          {sub && (
            <>
              {t("support.tickets.planLine", { plan: sub.plan.name, sla: slaLine() })}{" "}
              <Link className="text-cvp-accent underline" to="/support/plan">
                {t("support.tickets.changePlan")}
              </Link>
            </>
          )}
        </p>
        <button className="btn-primary shrink-0" onClick={() => setShowForm((v) => !v)}>
          {showForm ? t("common.cancel") : t("support.tickets.newTicket")}
        </button>
      </div>

      {showForm && <NewTicketForm onDone={() => setShowForm(false)} />}

      {isLoading ? (
        <p className="text-nb-text-2">{t("common.loading")}</p>
      ) : !tickets?.length ? (
        <p className="text-nb-text-2">{t("support.tickets.empty")}</p>
      ) : (
        <ul className="space-y-2">
          {tickets.map((tk) => (
            <li key={tk.id}>
              <Link
                to={`/support/tickets/${tk.id}`}
                className="card flex items-center justify-between hover:border-cvp-accent"
              >
                <div>
                  <div className="font-medium text-nb-text">{tk.subject}</div>
                  <div className="text-xs text-nb-text-2">
                    #{tk.id.slice(0, 8)} · {t(`support.category.${tk.category}`)} ·{" "}
                    {t(`support.priority.${tk.priority}`)} ·{" "}
                    {formatDateTime(tk.created_at)}
                  </div>
                </div>
                <TicketStatusPill status={tk.status} />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </AppLayout>
  );
}

function NewTicketForm({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  const create = useCreateTicket();
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [priority, setPriority] = useState("normal");
  const [category, setCategory] = useState("general");

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!subject.trim() || !body.trim()) return;
    await create.mutateAsync({ subject, body, priority, category });
    onDone();
  };

  return (
    <form onSubmit={submit} className="card space-y-3">
      <div>
        <label className="label" htmlFor="subject">{t("support.form.subject")}</label>
        <input id="subject" className="input" value={subject}
          onChange={(e) => setSubject(e.target.value)} required />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label" htmlFor="category">{t("support.form.category")}</label>
          <select id="category" className="input" value={category}
            onChange={(e) => setCategory(e.target.value)}>
            {["general", "technical", "billing", "account", "other"].map((c) => (
              <option key={c} value={c}>{t(`support.category.${c}`)}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="label" htmlFor="priority">{t("support.form.priority")}</label>
          <select id="priority" className="input" value={priority}
            onChange={(e) => setPriority(e.target.value)}>
            {["low", "normal", "high", "urgent"].map((p) => (
              <option key={p} value={p}>{t(`support.priority.${p}`)}</option>
            ))}
          </select>
        </div>
      </div>
      <div>
        <label className="label" htmlFor="body">{t("support.form.body")}</label>
        <textarea id="body" className="input" rows={5} value={body}
          onChange={(e) => setBody(e.target.value)} required />
      </div>
      <button type="submit" className="btn-primary" disabled={create.isPending}>
        {create.isPending ? t("support.form.submitting") : t("support.form.submit")}
      </button>
    </form>
  );
}
